import React, { useState } from 'react';
import FormControl from '../../common/FormControl';
import Button from '../../common/Button';
import { Send, ChevronDown } from 'lucide-react';
import type { LitInstance, LitHistoryEntry } from '../../../services/litInstanceService';
import type { LitProceedingGraph, LitGraphStep } from '../../../services/litigationMasterService';
import RecordStepModal from './RecordStepModal';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import NoDataFound from '../../common/NoDataFound';

const CLASS_COLORS: Record<string, { dot: string; badge: string }> = {
  orange: { dot: 'bg-orange-400',  badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  blue:   { dot: 'bg-blue-400',    badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  yellow: { dot: 'bg-yellow-400',  badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  purple: { dot: 'bg-purple-400',  badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  green:  { dot: 'bg-green-400',   badge: 'bg-green-50 text-green-700 border-green-200' },
};
const dotColor  = (c?: string | null) => CLASS_COLORS[c || '']?.dot  || 'bg-gray-400';
const badgeClass = (c?: string | null) => CLASS_COLORS[c || '']?.badge || 'bg-gray-100 text-gray-700 border-gray-200';

interface Props {
  instance: LitInstance;
  history: LitHistoryEntry[];
  graph: LitProceedingGraph | null;
  canAdvance: boolean;
  onAdvance: (payload: {
    step_id?: number | null;
    step_label: string;
    step_class_name?: string | null;
    step_class_color?: string | null;
    notes?: string;
    next_due_date?: string | null;
  }) => Promise<void>;
  mode?: 'grc' | 'execute';
}

const LitTimeline: React.FC<Props> = ({ instance, history, graph, canAdvance, onAdvance, mode = 'grc' }) => {
  const { formatDate } = useEntitySettings();
  const [recordModalOpen, setRecordModalOpen] = useState(false);

  const isTemplateMode = !instance.is_free_style && !!instance.proceeding_id;
  const currentStepInGraph = graph?.steps.find(s => s.id === instance.current_step_id);
  const isTerminal = !!currentStepInGraph?.is_terminal;
  const isExecute = mode === 'execute';

  return (
    <div className="flex flex-col gap-y-4">

      {/* ── Expected step sequence (template) — execute persona only ── */}
      {isExecute && isTemplateMode && graph && graph.steps.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
          <p className="text-[11px] font-semibold text-gray-500 mb-2">
            Expected steps — {graph.proceeding_name}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {graph.steps.map((s, i) => (
              <React.Fragment key={s.id}>
                <span
                  className={`inline-flex items-center text-[11px] px-2 py-1 rounded-lg border ${
                    s.id === instance.current_step_id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : badgeClass(s.step_class_color)
                  }`}
                  data-testid={`CHIP_lit_expected_step_${s.id}`}
                >
                  {s.step_name}
                </span>
                {i < graph.steps.length - 1 && (
                  <span className="text-gray-300 text-xs">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* ── Record Next Step Button ──────────────────────────────── */}
      {canAdvance && !isTerminal && (
        <div className="flex justify-end mb-4">
          <Button
            variant="fill"
            onClick={() => setRecordModalOpen(true)}
            data-testid="BTN_lit_record_next_step"
          >
            {history.length === 0 ? 'Record initial step' : 'Record next step'}
          </Button>
        </div>
      )}

      {/* ── History timeline ──────────────────────────────────────── */}
      {history.length === 0 ? (
        <NoDataFound title="No Steps Recorded" message="No steps have been recorded for this matter yet." />
      ) : (
        <div className="relative pl-6">
          {/* vertical line */}
          <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200 border-l border-dashed border-gray-300" />

          <div className="flex flex-col gap-y-5">
            {history.map((entry, idx) => (
              <div key={entry.id} className="relative">
                {/* dot */}
                <span className={`absolute -left-6 top-1.5 w-3 h-3 rounded-full border-2 border-white shadow-sm ${dotColor(entry.step_class_color)}`} />

                <div className="pb-1">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {entry.step_class_name && (
                        <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full border ${badgeClass(entry.step_class_color)}`}>
                          {entry.step_class_name}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-gray-800">{entry.step_label}</span>
                      {idx === 0 && (
                        <span className="status-badge sm info">Current</span>
                      )}
                    </div>
                    {entry.next_due_date && (
                      <span className={`text-[11px] shrink-0 ${idx === 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                        Next Due: {formatDate(entry.next_due_date)}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    by <span className="text-gray-600">{entry.recorded_by_name || '—'}</span>
                    {' on '}{formatDate(entry.recorded_on)}
                    {entry.notes && <span className="text-gray-600"> — {entry.notes}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <RecordStepModal
        isOpen={recordModalOpen}
        onClose={() => setRecordModalOpen(false)}
        onSubmit={onAdvance}
        instance={instance}
        graph={graph}
        isTemplateMode={isTemplateMode}
        mode={mode}
        history={history}
      />
    </div>
  );
};

export default LitTimeline;
